const CMD_PAYER_PREFIX = 'collaboratemd://payer/';
/** Absolute FHIR Organization references (terminology catalog, Aidbox absolute URLs, etc.). */
const ORGANIZATION_PATH_RE = /\/Organization\/([^/?#]+)$/i;

/**
 * Parse a coverage's payor URI into its type and ID.
 * @param {object} coverage - A FHIR Coverage resource
 * @returns {{ type: 'collaboratemd'|'payer'|'org'|null, id: string|null }}
 */
export function parsePayorUri(coverage) {
    const uri = coverage?.payor?.[0]?.uri;
    if (!uri) return { type: null, id: null };
    if (uri.startsWith(CMD_PAYER_PREFIX)) {
        return { type: 'collaboratemd', id: uri.replace(CMD_PAYER_PREFIX, '') };
    }
    const orgMatch = uri.match(ORGANIZATION_PATH_RE);
    if (orgMatch) {
        // Catalog / absolute FHIR org refs use the Organization id as the payer id key
        // (e.g. .../Organization/00053 → fetch Organization?type=pay&payerId=00053).
        return { type: 'payer', id: orgMatch[1] };
    }
    if (!uri.includes('://')) {
        return { type: 'org', id: uri };
    }
    return { type: null, id: null };
}

/**
 * Collect all CollaborateMD / catalog payer IDs and plain org reference IDs from coverages.
 * @param {object[]} coverages - Array of FHIR Coverage resources
 * @returns {{ payerIds: Set<string>, orgRefIds: Set<string> }}
 */
export function extractPayorIds(coverages) {
    const payerIds = new Set();
    const orgRefIds = new Set();
    for (const cov of coverages) {
        const { type, id } = parsePayorUri(cov);
        if (type === 'collaboratemd' || type === 'payer') payerIds.add(id);
        else if (type === 'org') orgRefIds.add(id);
    }
    return { payerIds, orgRefIds };
}

/**
 * Build a map of coverage ID → organization name by resolving payor URIs
 * against lookup tables keyed by CollaborateMD payerId and FHIR org id.
 * @param {object[]} coverages - Array of FHIR Coverage resources
 * @param {Object<string, string>} orgByPayerId - Map of CollaborateMD payerId → org name
 * @param {Object<string, string>} orgById - Map of FHIR Organization id → org name
 * @returns {Object<string, string>} Map of coverage id → org name
 */
export function buildCoveragePayorNameMap(coverages, orgByPayerId, orgById = {}) {
    const nameMap = {};
    for (const cov of coverages) {
        const { type, id } = parsePayorUri(cov);
        if (type === 'collaboratemd' || type === 'payer') {
            const name = orgByPayerId[id] || orgById[id];
            if (name) nameMap[cov.id] = name;
        } else if (type === 'org' && orgById[id]) {
            nameMap[cov.id] = orgById[id];
        }
    }
    return nameMap;
}

/** Ids per Organization search, so a long payer list can't produce an oversized search URL. */
export const PAYER_SEARCH_CHUNK_SIZE = 80;

/**
 * Look up the current Organization name for each coverage's payer, in chunked searches by payer
 * ID and by Organization id. A chunk that fails is logged and skipped, so the other chunks still
 * resolve and the coverages it would have named keep their stored names.
 *
 * @param {object} httpFhirApi - injected FHIR HTTP client
 * @param {object[]} coverages - Coverage-shaped entries (`id` plus `payor[0].uri`)
 * @param {object} [options]
 * @param {object[]} [options.knownOrganizations] - Organizations already in hand (e.g. from an
 *   `_include`); they are not searched for by id again
 * @param {object} [options.requestConfig] - passed to every search request
 * @returns {Promise<Object<string, string>>} Map of coverage id → current payer name
 */
export async function fetchCurrentPayerNames(httpFhirApi, coverages, { knownOrganizations = [], requestConfig } = {}) {
    const { payerIds, orgRefIds } = extractPayorIds(coverages || []);
    for (const organization of knownOrganizations) orgRefIds.delete(organization.id);

    const search = async (prefix, chunk) => {
        try {
            const response = await httpFhirApi.get(`${prefix}${chunk.map(encodeURIComponent).join(',')}&_count=1000`, requestConfig);
            return Array.isArray(response?.data) ? response.data : [];
        } catch (error) {
            console.error('Error fetching current payer names', { operation: 'fetchCurrentPayerNames', search: prefix, ids: chunk, error });
            return [];
        }
    };
    const searches = [];
    for (const [prefix, ids] of [
        ['Organization?type=pay&payerId=', [...payerIds]],
        ['Organization?_id=', [...orgRefIds]],
    ]) {
        for (let i = 0; i < ids.length; i += PAYER_SEARCH_CHUNK_SIZE) searches.push(search(prefix, ids.slice(i, i + PAYER_SEARCH_CHUNK_SIZE)));
    }
    const organizations = [...knownOrganizations, ...(await Promise.all(searches)).flat()];

    const orgByPayerId = {};
    const orgById = {};
    for (const organization of organizations) {
        if (organization.payerId) orgByPayerId[organization.payerId] = organization.name;
        orgById[organization.id] = organization.name;
    }
    return buildCoveragePayorNameMap(coverages || [], orgByPayerId, orgById);
}

/**
 * A payer record's payer ID, trimmed; '' when it has none. The payer-record lookup and the
 * equivalence match both read payer IDs through this, so they agree on what counts as the same ID.
 *
 * @param {object|null|undefined} organization
 * @returns {string}
 */
function payerIdOf(organization) {
    return String(organization?.payerId || '').trim();
}

/**
 * A coverage whose payer was saved from the account's own payer list points at the Organization
 * id (the `org` type), because that payer had no payer ID then. If the Organization has since
 * been given one, return the payor entry re-keyed to `collaboratemd://payer/<payerId>` — the
 * form every claim and eligibility path reads the payer ID from. Otherwise return null.
 *
 * @param {object} coverage - A FHIR Coverage (plain or model)
 * @param {object|null|undefined} organization - The Organization the coverage's `org` uri names
 * @returns {object|null} a new plain payor entry, or null when there is nothing to change
 */
export function payorWithOrganizationPayerId(coverage, organization) {
    const { type, id } = parsePayorUri(coverage);
    const payerId = payerIdOf(organization);
    if (type !== 'org' || !payerId || organization?.id !== id) return null;
    const payor = coverage.payor[0];
    const plain = typeof payor?.toJSON === 'function' ? payor.toJSON() : { ...payor };
    return { ...plain, uri: `${CMD_PAYER_PREFIX}${payerId}` };
}

/**
 * Every payer reference that names the same payer as `payor`. One payer can be referenced two
 * ways: by its payer-record id (the `org` type), and, once that record has a payer ID, as
 * `collaboratemd://payer/<payerId>` — the form payorWithOrganizationPayerId rewrites a coverage
 * to. `organizations` are the payer records looked up for `payor`; records sharing its payer ID
 * are included, because the payer list does not enforce unique payer IDs.
 *
 * A catalog (…/Organization/<id>) reference yields only itself, and a payor with no uri yields
 * an empty set: two payers without a uri are never the same payer.
 *
 * @param {object|null|undefined} payor - A payor entry ({ uri, display })
 * @param {object[]|null|undefined} organizations - Payer Organizations looked up for that payor
 * @returns {Set<string>}
 */
export function equivalentPayorUris(payor, organizations) {
    const uris = new Set();
    if (!payor?.uri) return uris;
    uris.add(payor.uri);

    const { type, id } = parsePayorUri({ payor: [payor] });
    const orgs = Array.isArray(organizations) ? organizations : [];

    const payerIds = new Set();
    if (type === 'collaboratemd' && id) payerIds.add(id);
    if (type === 'org') {
        for (const org of orgs) {
            if (org?.id === id && payerIdOf(org)) payerIds.add(payerIdOf(org));
        }
    }

    for (const org of orgs) {
        if (!org?.id) continue;
        if ((type === 'org' && org.id === id) || payerIds.has(payerIdOf(org))) uris.add(org.id);
    }
    for (const payerId of payerIds) uris.add(`${CMD_PAYER_PREFIX}${payerId}`);
    return uris;
}

/**
 * The form of a payer reference that can name the same payer as a reference in another form, by way
 * of a payer record: 'org' (a bare payer-record id) or 'collaboratemd' (a payer ID). Any other
 * reference — a catalog uri, or none — names only itself, so equivalence never needs records for it.
 *
 * @param {string|null|undefined} uri
 * @returns {'org'|'collaboratemd'|null}
 */
function recordLinkedForm(uri) {
    const { type } = parsePayorUri({ payor: [{ uri }] });
    return type === 'org' || type === 'collaboratemd' ? type : null;
}

/**
 * Look up the payer records equivalentPayorUris needs to recognize every reference form of these
 * payors, in chunked searches. A bare payer-record id is searched by id, then by the payer ID that
 * record carries; a collaboratemd:// payor is searched by its payer ID. A catalog reference or a
 * payor with no uri needs no records, so it makes no request. A failed search is logged and
 * contributes nothing, and the payors it was for are not cached; this never throws.
 *
 * @param {object} httpFhirApi - injected FHIR HTTP client
 * @param {object[]} payors - payor entries ({ uri, display }); null entries are ignored
 * @param {object} [options]
 * @param {Map<string, object[]>} [options.cache] - records found per payor uri, shared across
 *   calls (e.g. one claim submission); a payor uri already in it is not searched again
 * @returns {Promise<object[]>} payer Organizations, unique by id
 */
export async function fetchEquivalentPayerOrganizations(httpFhirApi, payors, { cache } = {}) {
    const found = [];
    const uncached = new Map();
    for (const payor of payors || []) {
        const uri = payor?.uri;
        if (!uri || uncached.has(uri)) continue;
        if (cache?.has(uri)) {
            found.push(...cache.get(uri));
            continue;
        }
        if (recordLinkedForm(uri)) uncached.set(uri, parsePayorUri({ payor: [payor] }));
    }

    const search = async (prefix, ids) => {
        const failed = new Set();
        const chunks = [];
        for (let i = 0; i < ids.length; i += PAYER_SEARCH_CHUNK_SIZE) chunks.push(ids.slice(i, i + PAYER_SEARCH_CHUNK_SIZE));
        const results = await Promise.all(
            chunks.map(async (chunk) => {
                try {
                    const response = await httpFhirApi.get(`${prefix}${chunk.map(encodeURIComponent).join(',')}&_count=1000`, { errorHandle: false });
                    return Array.isArray(response?.data) ? response.data : [];
                } catch (error) {
                    console.error('Error looking up payer records', { operation: 'fetchEquivalentPayerOrganizations', ids: chunk, error });
                    chunk.forEach((id) => failed.add(id));
                    return [];
                }
            }),
        );
        return { records: results.flat(), failed };
    };

    const lookups = [...uncached.values()];
    const byId = await search('Organization?_id=', [...new Set(lookups.filter(({ type }) => type === 'org').map(({ id }) => id))]);
    const searchedPayerIds = new Set([...lookups.filter(({ type }) => type === 'collaboratemd').map(({ id }) => id), ...byId.records.map(payerIdOf).filter(Boolean)]);
    const byPayerId = await search('Organization?type=pay&payerId:exact=', [...searchedPayerIds]);
    const withPayerId = (payerId) => byPayerId.records.filter((organization) => payerIdOf(organization) === payerId);

    for (const [uri, { type, id }] of uncached) {
        const record = type === 'org' ? byId.records.find((organization) => organization.id === id) : null;
        const payerId = type === 'org' ? payerIdOf(record) : id;
        const records = [...(record ? [record] : []), ...(payerId ? withPayerId(payerId) : [])];
        const complete = !(type === 'org' && byId.failed.has(id)) && !(payerId && byPayerId.failed.has(payerId));
        found.push(...records);
        if (cache && complete) cache.set(uri, records);
    }
    return [...new Map(found.map((organization) => [organization.id, organization])).values()];
}

/**
 * Whether an authorization naming its payer by one of `insurerUris` could name the same payer as
 * one of `payors` by a different reference, so matching them needs payer records. Two references
 * can only be forms of one payer when both have a recordLinkedForm, and not two different
 * collaboratemd:// payer IDs.
 *
 * By default every payor is checked, not just one: authorization lists match coverage by coverage,
 * so an authorization matching one coverage exactly can still belong under another coverage for
 * the same payer saved in another form. A caller that takes an exact match as settling an
 * authorization (it needs one coverage per authorization, not every one) passes
 * `exactMatchSettles`, and only authorizations matching no payor exactly are checked.
 *
 * @param {object[]} payors - payor entries ({ uri, display }); null entries are ignored
 * @param {Array<string|null|undefined>} insurerUris - the authorizations' insurer uris
 * @param {object} [options]
 * @param {boolean} [options.exactMatchSettles=false]
 * @returns {boolean}
 */
export function payerRecordsNeeded(payors, insurerUris, { exactMatchSettles = false } = {}) {
    const payorUris = [...new Set((payors || []).map((payor) => payor?.uri).filter(recordLinkedForm))];
    const unsettled = (insurerUri) => recordLinkedForm(insurerUri) && !(exactMatchSettles && payorUris.includes(insurerUri));
    const differentPayerIds = (a, b) => recordLinkedForm(a) === 'collaboratemd' && recordLinkedForm(b) === 'collaboratemd';
    return [...new Set((insurerUris || []).filter(unsettled))].some((insurerUri) => payorUris.some((payorUri) => payorUri !== insurerUri && !differentPayerIds(payorUri, insurerUri)));
}

/**
 * The payer records needed to match authorizations to these payors by payer equivalence (see
 * equivalentPayorUris): fetchEquivalentPayerOrganizations when payerRecordsNeeded, otherwise no
 * request and no records, which leaves the exact match. Never throws.
 *
 * @param {object} httpFhirApi - injected FHIR HTTP client
 * @param {object[]} payors - the coverages' payor entries
 * @param {Array<string|null|undefined>} insurerUris - the authorizations' insurer uris
 * @param {object} [options]
 * @param {boolean} [options.exactMatchSettles] - see payerRecordsNeeded
 * @param {Map<string, object[]>} [options.cache] - see fetchEquivalentPayerOrganizations
 * @returns {Promise<object[]>} payer Organizations, unique by id
 */
export async function fetchPayerRecordsForMatching(httpFhirApi, payors, insurerUris, { exactMatchSettles, cache } = {}) {
    if (!payerRecordsNeeded(payors, insurerUris, { exactMatchSettles })) return [];
    return fetchEquivalentPayerOrganizations(httpFhirApi, payors, { cache });
}
